import { PushService } from '../services/PushService';

/**
 * Aviso de lead novo no aparelho (Web Push), pelo service worker `/sw.js`.
 *
 * Até 16/10/2026 o único aviso do sistema era um `new Notification()` disparado pela aba aberta:
 * no Android ele lança erro (o Chrome só aceita aviso pelo service worker), no iPhone não existe em
 * aba comum, e com a tela bloqueada a página nem roda. Ou seja, no celular nada chegava.
 *
 * O iPhone só libera push para o app instalado na Tela de Início (iOS 16.4+); no Safari comum o
 * `PushManager` nem existe. Por isso o estado "instalar-iphone" tem passo a passo próprio.
 */

export type EstadoAviso =
    /** Ativo neste aparelho, para este usuário. */
    | 'ativo'
    /** Dá para ativar: falta o toque no botão. */
    | 'desligado'
    /** A pessoa negou a permissão; só se libera nas configurações do navegador. */
    | 'bloqueado'
    /** iPhone no Safari: precisa adicionar à Tela de Início antes. */
    | 'instalar-iphone'
    /** Navegador sem push, ou ambiente sem chave configurada. */
    | 'indisponivel';

const ehIOS = () =>
    /iphone|ipad|ipod/i.test(navigator.userAgent)
    // iPad com iPadOS se apresenta como Mac; o toque denuncia.
    || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);

const instalado = () =>
    window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;

const temPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Registra o service worker. Chamado uma vez ao abrir o app; sem ele nada de push funciona. */
export const registrarServiceWorker = () => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').catch(() => { /* sem SW o app segue igual, só sem aviso */ });
};

let chaveEmCache: Promise<string> | null = null;
const chavePublica = () => {
    chaveEmCache ??= PushService.chavePublica().catch(() => '');
    return chaveEmCache;
};

/** A chave VAPID vem em base64url; o navegador quer os bytes. */
const bytesDaChave = (base64url: string) => {
    const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
    const bruto = atob(base64);
    const bytes = new Uint8Array(bruto.length);
    for (let i = 0; i < bruto.length; i++) bytes[i] = bruto.charCodeAt(i);
    return bytes;
};

const inscricaoAtual = async (): Promise<PushSubscription | null> => {
    const reg = await navigator.serviceWorker.getRegistration();
    return reg ? reg.pushManager.getSubscription() : null;
};

export const estadoDoAviso = async (): Promise<EstadoAviso> => {
    if (!temPush()) return ehIOS() && !instalado() ? 'instalar-iphone' : 'indisponivel';
    if (!(await chavePublica())) return 'indisponivel';
    if (Notification.permission === 'denied') return 'bloqueado';
    if (Notification.permission === 'granted' && (await inscricaoAtual())) return 'ativo';
    return 'desligado';
};

/**
 * Pede a permissão e inscreve o aparelho. Precisa vir de um toque: o iPhone recusa o pedido de
 * permissão fora de um gesto da pessoa.
 */
export const ativarAviso = async (): Promise<EstadoAviso> => {
    const chave = await chavePublica();
    if (!temPush() || !chave) return 'indisponivel';

    const permissao = await Notification.requestPermission();
    if (permissao !== 'granted') return permissao === 'denied' ? 'bloqueado' : 'desligado';

    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    const inscricao = (await reg.pushManager.getSubscription())
        ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytesDaChave(chave) });

    return (await PushService.inscrever(inscricao.toJSON())) ? 'ativo' : 'desligado';
};

export const desativarAviso = async (): Promise<EstadoAviso> => {
    const inscricao = await inscricaoAtual();
    if (inscricao) {
        await PushService.remover(inscricao.endpoint).catch(() => { });
        await inscricao.unsubscribe().catch(() => false);
    }
    return 'desligado';
};

/**
 * Ao entrar no app: se este aparelho já tem inscrição, ela passa a ser de quem está logado agora
 * (o mesmo navegador pode ter tido outra pessoa). Silencioso — é manutenção, não pergunta.
 */
export const sincronizarAviso = async () => {
    try {
        if (!temPush() || Notification.permission !== 'granted') return;
        const inscricao = await inscricaoAtual();
        if (inscricao) await PushService.inscrever(inscricao.toJSON());
    } catch { /* aviso é acessório: nunca atrapalha a entrada */ }
};

/** Ao sair: o aparelho para de receber os leads de quem saiu. A permissão do navegador fica. */
export const soltarAvisoNoLogout = async () => {
    try {
        if (!temPush()) return;
        const inscricao = await inscricaoAtual();
        if (inscricao) await PushService.remover(inscricao.endpoint);
    } catch { /* idem */ }
};
